import { Column } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ChevronsUpDown, EyeOff } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

type HeaderAlign = "left" | "center" | "right";

interface ColumnHeaderProps<TData, TValue>
  extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>;
  title: string;
  align?: HeaderAlign;
}

const ALIGN_CLASS: Record<HeaderAlign, string> = {
  left: "justify-start",
  center: "justify-center",
  right: "justify-end",
};

const TEXT_ALIGN_CLASS: Record<HeaderAlign, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

/** Cabeçalho ordenável no padrão do projeto (asc/desc/ocultar). */
export function ColumnHeader<TData, TValue>({
  column,
  title,
  className,
  align = "left",
}: ColumnHeaderProps<TData, TValue>) {
  if (!column.getCanSort()) {
    return <div className={cn(TEXT_ALIGN_CLASS[align], className)}>{title}</div>;
  }

  return (
    <div
      className={cn(
        "flex items-center space-x-2",
        ALIGN_CLASS[align],
        className,
      )}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost_disabled"
            size="sm"
            className={cn(
              "h-8 gap-4 border-none bg-transparent data-[state=open]:bg-accent",
              align === "left" ? "-ml-3" : "mx-0",
            )}
          >
            <span>{title}</span>
            {column.getIsSorted() === "desc" ? (
              <ArrowDown className="h-4 w-4" />
            ) : column.getIsSorted() === "asc" ? (
              <ArrowUp className="h-4 w-4" />
            ) : (
              <ChevronsUpDown className="h-4 w-4" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onClick={() => column.toggleSorting(false)}>
            <ArrowUp className="mr-1 h-3.5 w-3.5 text-muted-foreground/70" />
            Crescente
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => column.toggleSorting(true)}>
            <ArrowDown className="mr-1 h-3.5 w-3.5 text-muted-foreground/70" />
            Decrescente
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => column.toggleVisibility(false)}>
            <EyeOff className="mr-1 h-3.5 w-3.5 text-muted-foreground/70" />
            Ocultar
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
